import { createApi } from '@reduxjs/toolkit/query/react'
import axios from 'axios'
import cogoToast from "cogo-toast";
import { themeEndpoints } from "../app/modules/ThemeManagement/_redux/endpoints";
import { exchangeRateEndpoints } from "../app/modules/ExchangeRateManagement/_redux/endpoints";
import { migrationEndpoints } from "../app/modules/Migration/_redux/endpoints";
import { orderEndpoints } from "../app/modules/OrderManagement/_redux/endpoints";
import { bankEndpoints } from "../app/modules/BankManagement/_redux/endpoints";
import { usersEndpoints, accountEndpoints } from "../app/modules/UserManagement/_redux/endpoints";
import { transactionEndpoints } from "../app/modules/TransactionManagement/_redux/endpoints";
import { errorMessage } from "../app/util/Helpers";
import { paymentEndpoints } from "../app/modules/PaymentManagement/_redux/endpoints";
import { holdingsEndpoints } from "../app/modules/HoldingManagement/_redux/endpoints";
import { pendingKYCEndpoints } from "../app/modules/PendingKyc/_redux/endpoints";
import { actionEndpoints } from "../app/modules/Overview/_redux/pending-action/endpoints";
import { instrumentEndpoints } from '../app/modules/Instrument/_redux/endpoints';
import { reportEndpoints } from '../app/modules/Common/modals/reports/_redux/endpoints';
import { cardEndpoints } from '../app/modules/CardManagement/_redux/endpoints';
import { overviewEndpoints } from '../app/modules/Overview/_redux/overview/endpoints';
import { subscriptionEndpoints } from '../app/modules/SubscriptionManagement/_redux/endpoints';
import { giftCardEndpoints } from '../app/modules/GiftCardManagement/_redux/endpoints';
import { portfolioCreationEndpoints } from '../app/modules/PortfolioCreation/_redux/endpoints';
import { safeLockEndpoints } from '../app/modules/SafeLockManagement/_redux/endpoints';
import { socialEndpoints } from "../app/modules/SocialManagement/_redux/endpoints";
import { reportsEndpoints } from "../app/modules/ReportManagement/_redux/endpoints";
import { maintenanceFeeEndpoints } from '../app/modules/MaintenanceFeeManagement/_redux/endpoints';
import { gemsEndpoints } from "../app/modules/Gems/_redux/endpoints";
import { settingsEndpoints } from '../app/modules/Overview/_redux/endpoints';
import { stalledTransactionEndpoints } from '../app/modules/StalledTransactions/_redux/endpoints';
import { campaignEndpoints } from '../app/modules/CampaignManagement/_redux/endpoints';
import { balanceEndpoints } from '../app/modules/Overview/pages/balance/_redux/endpoints';
import { securityQuestionEndpoints } from '../app/modules/SecurityQuestionsManagement/_redux/endpoints';
import { commercialPaperEndpoints } from '../app/modules/Overview/_redux/commercial-papers/endpoints';
import { corporateAccountsEndpoints } from '../app/modules/CorporateAccounts/_redux/endpoints';

const axiosBaseQuery = ({ baseUrl } = { baseUrl: '' }) =>
    async ({ url, method, body, responseType }) => {
        try {
            const result = await axios({ url: baseUrl + url, method, data: body, responseType })
            return { data: result.data }
        } catch (axiosError) {
            let err = axiosError
            cogoToast.error(errorMessage(err))
            return {
                error: { status: err.response?.status, data: errorMessage(err) },
            }
        }
    }

export const oversightApi = createApi({
    reducerPath: 'oversightApi',
    baseQuery: axiosBaseQuery({
        baseUrl: '',
    }),
    tagTypes: [
        'Actions',
        'Banks',
        'Exchange Rates',
        'Themes',
        'Migrations',
        'Transactions',
        'Orders',
        'Payments',
        'Pending KYC',
        'User Pending KYC',
        "Trove Next",
        "Cards",
        "Reports",
        "Subscriptions",
        "Bonus Shares",
        "Safe Locks",
        "Social",
        "Maintenance Fee",
        "Gems",
        "settings",
        "stalled transaction",
        "campaigns",
        "security questions",
        "Eligible Shares",
        "Dividend Announcements",
        "Dividend Batches",
        "Commercial Papers",
        "Corporate Accounts"
    ],
    endpoints: (builder) => {
        return {
            ...overviewEndpoints(builder),
            ...actionEndpoints(builder),
            ...pendingKYCEndpoints(builder, getApi),
            ...usersEndpoints(builder),
            ...orderEndpoints(builder),
            ...transactionEndpoints(builder),
            ...paymentEndpoints(builder),
            ...themeEndpoints(builder),
            ...exchangeRateEndpoints(builder),
            ...migrationEndpoints(builder),
            ...bankEndpoints(builder),
            ...cardEndpoints(builder),
            ...holdingsEndpoints(builder),
            ...instrumentEndpoints(builder),
            ...reportEndpoints(builder),
            ...portfolioCreationEndpoints(builder),
            ...accountEndpoints(builder),
            ...subscriptionEndpoints(builder),
            ...giftCardEndpoints(builder),
            ...safeLockEndpoints(builder),
            ...socialEndpoints(builder),
            ...reportsEndpoints(builder),
            ...maintenanceFeeEndpoints(builder),
            ...gemsEndpoints(builder),
            ...settingsEndpoints(builder),
            ...stalledTransactionEndpoints(builder),
            ...campaignEndpoints(builder),
            ...securityQuestionEndpoints(builder),
            ...balanceEndpoints(builder),
            ...commercialPaperEndpoints(builder),
            ...corporateAccountsEndpoints(builder),
        }

    },
})

function getApi() {
    return oversightApi
}

export const {
    // Overview hooks
    useSetCookieMutation,

    // Actions hooks
    useDeleteActionMutation,
    useUpdateActionMutation,

    // Pending KYC hooks
    useGetAllPendingKycUserGroupsQuery,
    useGetUserPendingKycQuery,
    useUpdateUserPendingKycMutation,
    useUpdateUserRiskProfileMutation,
    useConfirmKycMutation,
    useDeclineKycMutation,
    useRejectKycMutation,
    useMoveToAssessedMutation,
    useMoveToPendingMutation,
    useInitiateCipCheckMutation,
    useApproveCipChecksMutation,
    useGetDocumentsQuery,
    useGetDocumentQuery,
    useApproveDocumentMutation,
    useUpdateDocumentFieldsMutation,
    useRejectDocumentMutation,
    useResetDocumentMutation,
    useDownloadKYCReportMutation,
    useApproveAddressUpdateMutation,
    useRejectAddressUpdateMutation,
    useGetAddressUpdateRequestsQuery,

    // Order hooks
    useGetOrdersQuery,
    useGetOrdersAggregateQuery,
    useGetOrderQuery,
    useDownloadOrdersReportMutation,
    useSubmitOrdersHistoryExportMutation,
    usePollOrdersHistoryExportMutation,
    useCreateOrderMutation,
    useUpdateOrderMutation,
    useCancelOrderMutation,
    useApproveOrderMutation,

    // Transaction hooks
    useGetTransactionsQuery,
    useGetTransactionSummaryQuery,
    useDownloadTransactionsReportMutation,
    useSubmitAccountsHistoryExportMutation,
    usePollAccountsHistoryExportMutation,
    useApproveTransactionMutation,
    useDeclineTransactionMutation,
    useAddTransactionMutation,

    // Payment hooks
    useGetPaymentsQuery,
    useGetWithdrawalRetriesQuery,
    useGetFailedWithdrawalsRetryableQuery,
    useSearchFailedWithdrawalsQuery,
    useGetPaymentQuery,
    useGetPaymentsSummaryQuery,
    useDownloadPaymentsReportMutation,
    useSubmitPaymentsHistoryExportMutation,
    usePollPaymentsHistoryExportMutation,
    useUpdatePaymentMutation,
    useApproveWithdrawalMutation,
    useApproveDepositMutation,
    useDeclineWithdrawalMutation,
    useAddPaymentMutation,
    useRetryFailedWithdrawalMutation,

    //Holdings Hooks
    useGetPositionsQuery,
    usePayoutDividendsMutation,
    usePayoutSelectiveDividendsMutation,
    useAppendBonusShareHistoryMutation,
    usePayBonusSharesMutation,
    usePaySelectiveBonusSharesMutation,
    useGetBonusSharesHistoryQuery,
    useGetEligibleSharesQuery,
    useGetDividendAnnouncementsQuery,
    useCreateDividendAnnouncementMutation,
    useGenerateDividendBatchMutation,
    useGetDividendBatchesQuery,
    useGetDividendBatchQuery,
    useUpdateDividendBatchRecordMutation,
    useApproveDividendBatchMutation,
    useExecuteDividendBatchMutation,
    useRecallDividendBatchMutation,
    useGetDividendBatchAuditQuery,

    // Card hooks
    useGetCardsQuery,
    useGetCardTransactionsQuery,
    useFreezeCardMutation,
    useUnfreezeCardMutation,
    useDownloadCardsReportMutation,
    useDownloadCardTransactionsReportMutation,

    // Theme hooks
    useGetThemesQuery,
    useGetThemeAssetsQuery,
    useUpdateThemeMutation,
    useUpdateThemeImagesMutation,
    useUpdateThemesOrderMutation,
    useCreateThemeMutation,
    useDeleteThemeMutation,

    // Exchange rates hooks
    useGetExchangeRatesQuery,
    useUpdateExchangeRateMutation,
    useCreateExchangeRateMutation,
    useDeleteExchangeRateMutation,

    //Migrations hooks
    useGetUserMigrationsQuery,
    useGetUserMigrationLogsQuery,
    useMigrateUserMutation,
    useResetUserMigrationMutation,

    //Banks Hooks
    useGetBanksQuery,
    useUpdateBankMutation,
    useCreateBankMutation,
    useDeleteBankMutation,

    //Users Hooks
    useGetUserProfileQuery,
    useGetUserPinStatusQuery,
    useGetUserGroupsQuery,
    useLazySearchUsersQuery,
    useMigrateToGroupMutation,
    useUpdateUserStatusMutation,
    useClearUserPinSuspensionMutation,
    useAllowUserPinResetMutation,

    //Instrument hooks
    useGetNigerianInstrumentsQuery,
    useUpdateInstrumentMutation,

    //Report Hooks
    useGetReportsQuery,
    useGenerateDownloadURLMutation,

    //Portfolio Creation Hooks
    useUpdatePortfolioCreationMutation,
    useDownloadPortfolioCreationReportMutation,

    //Account Hooks
    useUpdateAccountMetaMutation,
    useAppendAccountMutation,
    useTriggerAiAccountReviewMutation,
    useGetAiAccountReviewQuery,
    useLazyGetAiAccountReviewQuery,

    //Subscription Hooks
    useCreateSubscriptionMutation,
    useUpdateSubscriptionMutation,
    useRenewSubscriptionMutation,
    useCancelSubscriptionMutation,
    useDownloadSubscriptionReportMutation,
    useDownloadBillingReportMutation,


    //Gift Card Hooks
    useCreateGiftCardMutation,
    useUpdateGiftCardMutation,
    useCancelGiftCardMutation,

    //Safe Lock Hooks
    useGetSafeLocksQuery,
    useDownloadSafeLockReportMutation,
    useDownloadLiquidationReportMutation,
    useGetSafeLockPlansQuery,
    useGetDisabledPlansQuery,
    useCreatePlanMutation,
    useUpdatePlanMutation,
    useEnablePlanMutation,
    useDisablePlanMutation,
    useUpdateSafeLockStatusMutation,

    //Social Hooks
    useSyncSocialUserMutation,
    useGetSocialUserQuery,
    usePromoteUserMutation,
    useDemoteUserMutation,
    useGetSocialUserPostsQuery,
    useGetPostRepliesQuery,
    useGetSocialSubscriptionFeeQuery,
    useSetSocialSubscriptionFeeMutation,
    useSuspendUserMutation,
    useEnableUserMutation,
    useApproveSocialRequestMutation,
    useDeclineSocialRequestMutation,
    useDownloadSocialReportMutation,
    useGetSocialAnalyticsQuery,

    //Report Hooks
    useDownloadUsersByTradeActivityReportMutation,
    useDownloadUsersByHoldingTypeReportMutation,

    //Maintenance Fee Hooks
    useSearchAccountsQuery,
    useCreateMaintenanceFeeMutation,
    useCreateBillingMutation,
    useGetBillingHistoryQuery,
    useExemptAccountsMutation,
    useGetMaintenanceFeeSettingsQuery,
    useProcessBillingsMutation,

    //Market Status Hooks
    useSetMarketStatusMutation,

    // Gems hooks
    useGetGemsRewardConfigQuery,
    useUpdateGemsRewardConfigMutation,
    useGetGemsOverviewQuery,
    useGetGemsUserActivityQuery,
    useGetGemsCampaignPerformanceSummaryQuery,
    useGetGemsAccrualMonitoringQuery,
    useGetGemsTopCampaignsQuery,
    useGetGemsAccrualRedemptionTotalsQuery,
    useGetGemsTopUsersQuery,
    useGetGemsRewardAttemptsQuery,
    useGetGemsAuditLogsQuery,
    useGetGemsExchangeRateActiveQuery,
    useGetGemsExchangeRateHistoryQuery,
    useSetGemsExchangeRateMutation,
    useGetGemsUserBalanceQuery,
    useGetGemsUserRewardsQuery,
    useGetGemsUserRedemptionsQuery,
    useGetGemsUserCampaignsQuery,
    useGenerateGemsReportMutation,
    useSubmitGemsReportExportMutation,
    usePollGemsReportExportMutation,

    //Settings Hooks
    useGetCronsHealthReportQuery,
    useGetWebsiteHealthQuery,
    useTriggerPendingBvnMutation,
    useGetCommercialPapersQuery,
    useGetCommercialPaperSubscriptionsQuery,
    useCreateCommercialPaperMutation,
    useUpdateCommercialPaperMutation,
    useDeleteCommercialPaperMutation,
    useGenerateCommercialPaperSubscriptionsReportCsvMutation,
    useGenerateCommercialPaperSubscriptionsReportXlsxMutation,
    useGenerateCommercialPapersReportCsvMutation,
    useGenerateCommercialPapersReportXlsxMutation,

    //Stalled Transaction
    useGetStalledTransactionsQuery,
    useResolveTransactionMutation,
    useDownloadStalledTransactionsReportMutation,
    useBlockUserMutation,
    useGetTransactionAccountDetailsQuery,
    useGetActivityDetailsQuery,

     //Campaigns
     useCreateCampaignMutation,
     useUpdateCampaignMutation,
     useDeleteCampaignMutation,

      //Campaigns
     useGetBalanceQuery,

    //Security Questions
    useCreateSecurityQuestionMutation,
    useEditSecurityQuestionMutation,
    useDisableSecurityQuestionMutation,
    useGetUserSecurityQuestionsQuery,
    useSubmitUserSecurityQuestionAnswersMutation,

    // Trove Next
    useGetTroveNextProfilesQuery,
    useGetTroveNextProfileDocumentsQuery,
    useApproveTroveNextProfileDocumentMutation,
    useRejectTroveNextProfileDocumentMutation,
    useResetTroveNextProfileDocumentMutation,
    useGetTroveNextProfileTransitionStatusQuery,
    useActivateTroveNextProfileMutation,
    useApproveTroveNextProfileTransitionMutation,
    useUpdateTroveNextProfileMutation,

    // Corporate Accounts
    useGetCorporateApplicationsQuery,
    useGetCorporateApplicationQuery,
    useLazyGetCorporateApplicationQuery,
    useUpdateCorporateApplicationMutation,
    useCreateCorporateApplicationMutation,
    useGetCorporateApplicationEligibilityQuery,
    useGetCorporateAdminDocumentsQuery,
    useLazyGetCorporateAdminDocumentsQuery,
    useGetPendingBusinessKybAttemptsQuery,
    useApproveCorporateDocumentMutation,
    useUploadCorporateApplicationDocumentMutation,
    useApproveCorporateApplicationMutation,
    useRejectCorporateApplicationMutation,
    useAbandonCorporateApplicationMutation,
    useSubmitCorporateVerificationsMutation,
    useGetCorporateApplicationProvisionStatusQuery,
    useSyncCorporateApplicationProvisionMutation,
    useApproveCorporateW8beneMutation,
    useRejectCorporateW8beneMutation,
    useUnlockCorporateW8beneMutation,
    useGetCorporateW8beneQuery,
    useStartCorporateW8beneMutation,
    useSaveCorporateW8beneAnswersMutation,
    usePreviewCorporateW8beneMutation,
    useSignCorporateW8beneMutation,
    useSubmitCorporateW8beneMutation,
    useVerifyCorporateCacMutation,
    useManualVerifyCorporateCacMutation,
    useGetCorporateApplicationTinQuery,
    useLazyGetCorporateApplicationTinQuery,
    useGetCorporateApplicationPartiesQuery,
    useSaveCorporateApplicationPartiesMutation,
    useDeleteCorporateApplicationPartyMutation,
    useGetCorporateApplicationAgreementsQuery,
    useAcceptCorporateApplicationAgreementsMutation,
    useCreateCorporateAccountMutation,
    useRetryPendingBusinessKybAttemptsMutation,

    // Corporate Accounts — create-application wizard: user lookup
    useLookupAdminUsersQuery,
    useLazyLookupAdminUsersQuery,
    useLookupAdminUsersFilterQuery,
    useLazyLookupAdminUsersFilterQuery,
    useLookupAdminKycQuery,
    useLazyLookupAdminKycQuery,
} = oversightApi
